import WebSocketManager from './websocket-manager';
import type { WebSocketManagerConfig } from './types';
import { apidata } from '../../generated/prices';
import { WebsocketClientType } from '../constants/common';
import { WS_TRACKING_EVENTS } from '../constants/nr-events';

export type PriceCallback = (message: apidata.prices.PriceData) => void;

interface ISubscriptions {
  path: string;
  callback: PriceCallback;
  subId: string;
}

// Default protobuf decoder for prices
const defaultProtobufDecoder = (data: Uint8Array): apidata.prices.PriceData => {
  const PriceDataClass = apidata.prices.PriceData;
  return PriceDataClass.decode(data) as unknown as apidata.prices.PriceData;
};

export interface PricesWebSocketManagerConfig {
  websocketUrl: string;
  socketId: WebsocketClientType;
  protobufDecoder?: (data: Uint8Array) => apidata.prices.PriceData;
  websocketConfig: WebSocketManagerConfig;
}

class PricesWebSocketManager extends WebSocketManager {
  private static pricesInstance: PricesWebSocketManager | null = null;
  private pricesWebSocketUrl: string;
  private static setConnectionCallback: (
    ws: WebSocket | null,
    client: string,
  ) => void = () => {};
  private socketId: WebsocketClientType = WebsocketClientType.PRICES;
  private subscriptions: Map<string, ISubscriptions[]> = new Map();
  private connectionStartTime: number = 0;
  private isConnectionInProgress: boolean = false;
  private lastOnMessageError: string = '';
  private static natsConnectionStatus: string = 'closed';
  private protobufDecoder: (data: Uint8Array) => apidata.prices.PriceData;
  private static trackEventFn:
    | ((eventName: string, data: Record<string, unknown>) => void)
    | null = null;

  private constructor(config: PricesWebSocketManagerConfig) {
    super(config.websocketConfig);
    this.pricesWebSocketUrl = config.websocketUrl;
    this.socketId = config.socketId;
    this.protobufDecoder = config.protobufDecoder || defaultProtobufDecoder;
    // Store trackEvent from analytics config
    if (config.websocketConfig.analytics?.trackEvent) {
      PricesWebSocketManager.trackEventFn =
        config.websocketConfig.analytics.trackEvent;
    }
  }

  private trackPricesEvent(
    eventName: string,
    data: Record<string, unknown>,
  ): void {
    if (PricesWebSocketManager.trackEventFn) {
      PricesWebSocketManager.trackEventFn(eventName, {
        ...data,
        client: this.socketId,
      });
    }
  }

  static setNatsConnection(status: string) {
    this.natsConnectionStatus = status;
  }

  static getPricesInstance(
    config?: PricesWebSocketManagerConfig,
  ): PricesWebSocketManager {
    if (!this.pricesInstance) {
      if (!config) {
        throw new Error('Config is required for first initialization');
      }
      this.pricesInstance = new PricesWebSocketManager(config);
    }
    return this.pricesInstance;
  }

  static initializePricesSocketManager(
    config: PricesWebSocketManagerConfig,
    setConnectionCallback: (ws: WebSocket | null, client: string) => void,
    hashToken: string | null = null,
  ): void {
    if (!this.pricesInstance) {
      this.pricesInstance = new PricesWebSocketManager(config);
    }
    // Track initialization event
    if (PricesWebSocketManager.trackEventFn) {
      PricesWebSocketManager.trackEventFn(
        WS_TRACKING_EVENTS.INIT_SOCKET_MANAGER,
        {
          client: config.socketId,
          hasHashToken: !!hashToken,
          websocketUrl: config.websocketUrl,
        },
      );
    }
    super.initialize(hashToken);
    PricesWebSocketManager.setConnectionCallback = setConnectionCallback;
  }

  static getPricesWSInstance(): PricesWebSocketManager | null {
    if (!this.pricesInstance) {
      return null;
    }
    return this.pricesInstance;
  }

  async initializePriceConnection(): Promise<void> {
    try {
      if (this.isConnectionInProgress) {
        this.trackPricesEvent(
          WS_TRACKING_EVENTS.INIT_CONNECTION_ALREADY_IN_PROGRESS,
          {
            url: this.pricesWebSocketUrl,
          },
        );
        return;
      }
      this.isConnectionInProgress = true;
      this.trackPricesEvent(WS_TRACKING_EVENTS.INIT_CONNECTION_START, {
        url: this.pricesWebSocketUrl,
      });
      await super.initializeConnection(
        this.socketId,
        this.pricesWebSocketUrl,
        this.setupEventListeners.bind(this),
      );
      this.connectionStartTime = Date.now();
    } catch (error: unknown) {
      console.error(
        '[PricesWebSocketManager] Error initializing price connection:',
        error,
      );
      this.trackPricesEvent(WS_TRACKING_EVENTS.INIT_CONNECTION_ERROR, {
        url: this.pricesWebSocketUrl,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      this.isConnectionInProgress = false;
    }
  }

  async disconnectPriceConnection(): Promise<void> {
    void super.disconnect(this.socketId);
  }

  getPricesConnection(): WebSocket | null {
    return super.getSocket(this.socketId);
  }

  private setupEventListeners() {
    const socket = this.getPricesConnection();

    if (!socket) {
      return;
    }

    socket.onopen = () => {
      this.subscriptions.clear();

      // Ensure socket is in the static Map (may have been cleared by cleanup/HMR)
      super.ensureSocketInMap(this.socketId, socket);

      PricesWebSocketManager.setConnectionCallback(socket, this.socketId);

      super.resetReconnectAttempts(this.socketId);

      const connectionTime = super.getConnectionCreationTime(this.socketId);
      this.trackPricesEvent(WS_TRACKING_EVENTS.CONNECTED, {
        connectionTime,
        url: this.pricesWebSocketUrl,
      });
    };

    socket.onclose = (event) => {
      this.trackPricesEvent(WS_TRACKING_EVENTS.DISCONNECTED, {
        wasClean: event.wasClean,
        code: event.code,
        reason: event.reason,
        url: this.pricesWebSocketUrl,
      });

      PricesWebSocketManager.setConnectionCallback(null, this.socketId);

      if (!event.wasClean) {
        super.handleReconnection(this.socketId, 'onclose');
      }
    };

    socket.onmessage = async (event: MessageEvent) => {
      try {
        const arrayBuffer = await (event.data instanceof Blob
          ? event.data.arrayBuffer()
          : event.data);

        const uint8Array = new Uint8Array(arrayBuffer);
        const decodedMessage = this.protobufDecoder(uint8Array);
        const subPath = decodedMessage.subPath;
        if (!subPath) {
          console.error(
            '[PricesWebSocketManager] No subPath found in the message',
            decodedMessage,
          );
          this.trackPricesEvent(WS_TRACKING_EVENTS.MESSAGE_NO_SUBPATH, {
            message: JSON.stringify(decodedMessage),
          });
          return;
        }
        const subscriptions = this.subscriptions.get(subPath);
        if (!subscriptions) {
          console.error(
            '[PricesWebSocketManager] No subscriptions found for the subPath',
            subPath,
          );
          this.trackPricesEvent(WS_TRACKING_EVENTS.MESSAGE_NO_SUBSCRIPTION, {
            subPath,
          });
          return;
        }
        subscriptions.forEach((sub) => {
          try {
            sub.callback(decodedMessage);
          } catch (error: unknown) {
            console.error(
              '[PricesWebSocketManager] Error processing WebSocket message',
              error,
            );
            this.trackPricesEvent(WS_TRACKING_EVENTS.MESSAGE_CALLBACK_ERROR, {
              subPath,
              error: error instanceof Error ? error.message : String(error),
            });
          }
        });

        this.lastOnMessageError = '';
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        console.error(
          `Error processing WebSocket message --> ${this.socketId}`,
          errorMessage,
        );
        if (this.lastOnMessageError !== errorMessage) {
          this.lastOnMessageError = errorMessage;
          this.trackPricesEvent(WS_TRACKING_EVENTS.MESSAGE_PROCESSING_ERROR, {
            error: errorMessage,
          });
        }
      }
    };

    socket.onerror = (error) => {
      console.error(`WebSocket error --> ${this.socketId}`, error);
      this.trackPricesEvent(WS_TRACKING_EVENTS.ERROR, {
        url: this.pricesWebSocketUrl,
        error: String(error),
      });

      super.handleReconnection(this.socketId, 'onerror');
    };
  }

  subscribe(path: string, callback: PriceCallback): () => void {
    try {
      const subId = `sub-${path}-prices-${Math.random()
        .toString(8)
        .slice(2, 11)}`;
      const socket = this.getPricesConnection();
      if (!socket) {
        this.trackPricesEvent(WS_TRACKING_EVENTS.SUBSCRIBE_NO_CONNECTION, {
          path,
        });
        throw new Error('Prices connection not initialized');
      }

      const existingSubscriptions = this.subscriptions.get(path) ?? [];

      if (!existingSubscriptions.length) {
        if (socket.readyState == WebSocket.OPEN) {
          const subscribeMessage = {
            action: 'SUB',
            mode: 'P',
            entities: [`${path}`],
          };
          socket.send(JSON.stringify(subscribeMessage));
        }
      }

      this.subscriptions.set(path, [
        ...existingSubscriptions,
        { path, callback, subId },
      ]);

      this.trackPricesEvent(WS_TRACKING_EVENTS.SUBSCRIBE_SUCCESS, {
        path,
      });

      // Return unsubscribe function
      return () => {
        const subs = this.subscriptions.get(path);
        if (subs) {
          const filteredSubs = subs.filter((sub) => sub.subId !== subId);

          if (filteredSubs.length === 0) {
            if (socket.readyState === WebSocket.OPEN) {
              const unsubscribeMessage = {
                action: 'UNSUB',
                mode: 'P',
                entities: [`${path}`],
              };
              socket.send(JSON.stringify(unsubscribeMessage));
            }
            this.subscriptions.delete(path);
          } else {
            this.subscriptions.set(path, filteredSubs);
          }

          this.trackPricesEvent(WS_TRACKING_EVENTS.UNSUBSCRIBE_SUCCESS, {
            path,
          });
        }
      };
    } catch (err: unknown) {
      console.error('Error subscribing to price', err);
      this.trackPricesEvent(WS_TRACKING_EVENTS.SUBSCRIBE_ERROR, {
        path,
        error: err instanceof Error ? err.message : String(err),
      });

      return () => {};
    }
  }

  isSubscribed(path: string): boolean {
    return this.subscriptions.has(path);
  }
}

export default PricesWebSocketManager;
